// src/hooks/useHealthConnect.js
// Health Connect integration for NutriLithe and LiftLithe (Android native only).
//
// Uses @capacitor-community/health-connect plugin (loaded dynamically so the
// web build never tries to resolve native APIs).
//
// On web / PWA : isAvailable = false. All read functions return null.
//                writeNutritionToHealth is a silent no-op.
// On Capacitor : reads steps, active calories, heart rate series, weight,
//                height. (Sleep and Nutrition write-back not supported in
//                capacitor-health-connect@0.7.0 — deferred.)
//
// Google attribution requirement (mandatory per Health Connect branding policy):
//   Any UI surface that displays Health Connect data MUST include the text
//   "Powered by Health Connect" or the Health Connect logo.
//
// Auto-sync: on mount + every 15 minutes when on native.
// 7-day history persisted to localStorage key "np_health_history".

import { useState, useEffect, useRef, useCallback } from "react";
import { App } from "@capacitor/app";
import { supabase } from "../supabaseClient";
import { TODAY_ISO } from "../constants";

// Side-effect import: triggers registerPlugin('HealthConnect') which populates
// window.Capacitor.Plugins.HealthConnect with the native bridge proxy.
// Without this, getPlugin() always returns null and all HC calls silently fail.
import "capacitor-health-connect";

// ─── Detect Capacitor native context ──────────────────────────────────────────
// Mirrors the same check used in App.js and WorkoutApp.jsx.
function detectNative() {
  if (typeof window === "undefined") return false;
  return (
    window.location.protocol === "capacitor:" ||
    !!(window.Capacitor?.isNativePlatform?.())
  );
}

// ─── Access the plugin via Capacitor's global plugin registry ─────────────────
// The side-effect import above runs registerPlugin('HealthConnect'), which adds
// a proxy to window.Capacitor.Plugins.HealthConnect on native platforms.
let _pluginCache = undefined; // undefined = not yet attempted; null = failed
function getPlugin() {
  if (_pluginCache !== undefined) return _pluginCache;
  try {
    const plugin = window?.Capacitor?.Plugins?.HealthConnect;
    _pluginCache = plugin ?? null;
  } catch (e) {
    _pluginCache = null;
  }
  return _pluginCache;
}

// ─── Permissions ──────────────────────────────────────────────────────────────
// Valid RecordType values for capacitor-health-connect@0.7.0.
// SleepSession and Nutrition are NOT valid RecordTypes in this version.
const READ_PERMS  = [
  "Steps", "ActiveCaloriesBurned", "HeartRateSeries", "Weight", "Height",
];
const WRITE_PERMS = []; // No valid write RecordTypes in plugin v0.7.0

// ─── Date helpers ─────────────────────────────────────────────────────────────
function todayStartISO() {
  const d = new Date(); d.setHours(0, 0, 0, 0); return d.toISOString();
}
function yesterdayEveningISO() {
  const d = new Date(); d.setDate(d.getDate() - 1); d.setHours(20, 0, 0, 0);
  return d.toISOString();
}

// ─── localStorage helpers ─────────────────────────────────────────────────────
function lsGet(key, fallback) {
  try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback; }
  catch (_) { return fallback; }
}
function lsSet(key, val) {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch (_) {}
}

// =============================================================================
// useHealthConnect()
// =============================================================================
export function useHealthConnect() {
  const isNative = detectNative();

  const [isAvailable,   setIsAvailable]   = useState(false);
  const [permGranted,   setPermGranted]   = useState(false);
  const [permError,     setPermError]     = useState(false);
  const [syncing,       setSyncing]       = useState(false);
  const [lastSynced,    setLastSynced]    = useState(null);
  const [healthData,    setHealthData]    = useState({
    steps: null, activeCalories: null, heartRate: null,
    sleepHours: null, weight: null, height: null,
  });
  const [healthHistory, setHealthHistory] = useState(
    () => lsGet("np_health_history", [])
  );

  const syncIntervalRef = useRef(null);

  // ── Check Health Connect availability on mount ──────────────────────────────
  useEffect(() => {
    if (!isNative) return;
    (async () => {
      const hc = getPlugin();
      if (!hc) { console.warn('[HealthConnect] Plugin not found after import — native bridge may not be ready'); setIsAvailable(false); return; }
      try {
        const result = await hc.checkAvailability();
        setIsAvailable(result.availability === "Available");
      } catch (_) {
        setIsAvailable(false);
      }
    })();
  }, [isNative]);

  // ── Re-check permissions when app resumes from background ─────────────────
  // Handles the case where the user grants permissions in Health Connect and
  // then returns to the app — permGranted updates without requiring a re-tap.
  useEffect(() => {
    if (!isNative || !isAvailable) return;
    const listener = App.addListener("appStateChange", async (state) => {
      if (!state.isActive) return;
      const hc = getPlugin();
      if (!hc) return;
      try {
        const result = await hc.checkHealthPermissions({
          read: READ_PERMS, write: WRITE_PERMS,
        });
        const allGranted = READ_PERMS.every(p =>
          result.grantedPermissions.includes(p)
        );
        setPermGranted(allGranted);
      } catch (_) {
        // silently ignore
      }
    });
    return () => { listener.then(l => l.remove()); };
  }, [isNative, isAvailable]);

  // ── Auto-sync: on mount + every 15 min ────────────────────────────────────
  useEffect(() => {
    if (!isNative || !isAvailable || !permGranted) return;
    syncHealthData();
    syncIntervalRef.current = setInterval(syncHealthData, 15 * 60 * 1000);
    return () => clearInterval(syncIntervalRef.current);
  }, [isNative, isAvailable, permGranted]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Request all required permissions ──────────────────────────────────────
  // Known issue: capacitor-health-connect v0.7.0 may not capture the activity
  // result callback from the Health Connect permission dialog, returning
  // {grantedPermissions:[], hasAllPermissions:false} even when the user granted
  // permissions. Workaround: always follow up with checkHealthPermissions.
  async function requestPermissions() {
    const hc = getPlugin();
    if (!hc) { console.warn('[HealthConnect] Plugin not available — requestPermissions skipped'); return false; }
    try {
      // Step 1: Open the permission dialog
      const reqResult = await hc.requestHealthPermissions({
        read: READ_PERMS, write: WRITE_PERMS,
      });
      const reqGranted = READ_PERMS.every(p =>
        (reqResult.grantedPermissions || []).includes(p)
      );
      if (reqGranted) {
        setPermGranted(true);
        setPermError(false);
        return true;
      }

      // Step 2: Follow-up check — the permission dialog result callback may have
      // been lost ("Unable to find a Capacitor plugin to handle permission
      // requestCode"), but the permissions may still have been granted in HC.
      try {
        const checkResult = await hc.checkHealthPermissions({
          read: READ_PERMS, write: WRITE_PERMS,
        });
        const checkGranted = (checkResult.grantedPermissions || []).length > 0;
        const allGranted = READ_PERMS.every(p =>
          (checkResult.grantedPermissions || []).includes(p)
        );
        if (checkGranted) {
          setPermGranted(allGranted);
          setPermError(false);
          return true;
        }
      } catch (_) {
        // checkHealthPermissions failed — fall through to error
      }

      setPermGranted(false);
      setPermError(true);
      return false;
    } catch (_) {
      setPermError(true);
      return false;
    }
  }

  // ── Main sync: read all Health Connect records for today ───────────────────
  const syncHealthData = useCallback(async () => {
    if (!isNative) return;
    const hc = getPlugin();
    if (!hc) return;
    setSyncing(true);

    try {
      // Ensure permissions are granted
      const { grantedPermissions } = await hc.requestHealthPermissions({
        read: READ_PERMS, write: WRITE_PERMS,
      });
      if (!grantedPermissions.includes("Steps")) {
        setPermError(true);
        setSyncing(false);
        return;
      }
      setPermGranted(true);
      setPermError(false);

      const start = todayStartISO();
      const end   = new Date().toISOString();
      const prev  = yesterdayEveningISO();

      // Steps today
      let steps = null;
      try {
        const { records } = await hc.readRecords({
          type: "Steps", startTime: start, endTime: end,
        });
        steps = records.reduce((s, r) => s + (r.count || 0), 0);
      } catch (_) {}

      // Active calories burned today
      let activeCalories = null;
      try {
        const { records } = await hc.readRecords({
          type: "ActiveCaloriesBurned", startTime: start, endTime: end,
        });
        activeCalories = Math.round(
          records.reduce((s, r) => s + (r.energy?.inKilocalories || 0), 0)
        );
      } catch (_) {}

      // Latest heart rate (most recent sample from HeartRateSeries)
      let heartRate = null;
      try {
        const { records } = await hc.readRecords({
          type: "HeartRateSeries", startTime: start, endTime: end,
        });
        if (records.length > 0) {
          const last = records[records.length - 1];
          heartRate = last.samples?.[last.samples.length - 1]?.beatsPerMinute ?? null;
        }
      } catch (_) {}

      // Sleep: not supported in capacitor-health-connect@0.7.0 (SleepSession is not
      // a valid RecordType). Kept as null — deferred to a future plugin version.
      const sleepHours = null;

      // Latest weight — also sync to Supabase weight_logs
      let weight = null;
      try {
        const { records } = await hc.readRecords({
          type: "Weight", startTime: prev, endTime: end,
        });
        if (records.length > 0) {
          weight = records[records.length - 1].weight?.inKilograms ?? null;
          if (weight !== null) {
            const { data: { user } } = await supabase.auth.getUser();
            if (user) {
              await supabase.from("weight_logs").upsert(
                { user_id: user.id, date: TODAY_ISO(), weight_kg: Math.round(weight * 10) / 10 },
                { onConflict: "user_id,date" }
              );
            }
          }
        }
      } catch (_) {}

      // Latest height (in cm)
      let height = null;
      try {
        const { records } = await hc.readRecords({
          type: "Height", startTime: prev, endTime: end,
        });
        if (records.length > 0) {
          height = Math.round((records[records.length - 1].height?.inMeters ?? 0) * 100);
        }
      } catch (_) {}

      const newData = { steps, activeCalories, heartRate, sleepHours, weight, height };
      setHealthData(newData);
      setLastSynced(new Date());

      // Persist 7-day rolling history
      setHealthHistory(prev => {
        const today = TODAY_ISO();
        const filtered = (prev || []).filter(d => d.date !== today);
        const next = [{ date: today, ...newData }, ...filtered].slice(0, 7);
        lsSet("np_health_history", next);
        return next;
      });

    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("[useHealthConnect] syncHealthData error", e);
      }
    }

    setSyncing(false);
  }, [isNative]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Write a food log entry as a NutritionRecord ───────────────────────────
  // NOTE: Nutrition is NOT a valid RecordType in capacitor-health-connect@0.7.0.
  // This function is a no-op until the plugin supports it. Kept as a stub so
  // the call site in App.js insertLog() does not need to change.
  async function writeNutritionToHealth(_logEntry) {
    // NutritionRecord write not supported in capacitor-health-connect v0.7.0 — deferred
  }

  return {
    isNative,
    isAvailable,
    permGranted,
    permError,
    syncing,
    lastSynced,
    healthData,
    healthHistory,
    syncHealthData,
    writeNutritionToHealth,
    requestPermissions,
  };
}
